import React from "react";
import {
  Warning,
  CheckCircle,
  Info,
  DownloadSimple,
  UserPlus,
  SignIn,
  Key,
  UploadSimple,
  Copy,
} from "@phosphor-icons/react";
import {
  APP_PLATFORM_LOGO_MAP,
  APPLE_SEARCH_ADS_GUIDE_STEPS_IMAGES_MAPPER,
} from "../../../shared/constants";
import useFancybox from "../../../hooks/useFancyBox";

const RedirectLink = ({ href, text }: { href: string; text: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-blue-600 dark:text-blue-400 hover:underline"
  >
    {text}
  </a>
);

const StepImage = ({
  step,
  className,
}: {
  step: keyof typeof APPLE_SEARCH_ADS_GUIDE_STEPS_IMAGES_MAPPER;
  className?: string;
}) => (
  <div className="w-max mt-3">
    <img
      src={APPLE_SEARCH_ADS_GUIDE_STEPS_IMAGES_MAPPER[step].link}
      alt="Apple Search Ads"
      data-fancybox="apple-search-ads-guide"
      data-src={APPLE_SEARCH_ADS_GUIDE_STEPS_IMAGES_MAPPER[step].link}
      className={`max-w-[900px] w-full h-auto border border-gray-200 dark:border-gray-700 cursor-zoom-in rounded-xl shadow-sm my-2 ${className ?? ""}`}
    />
  </div>
);

const StepCard = ({
  number,
  icon: Icon,
  title,
  children,
}: {
  number: number;
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) => (
  <div className="border border-gray-100 dark:border-gray-700 rounded-xl p-5 bg-white dark:bg-gray-800 shadow-sm space-y-3">
    <div className="flex items-center gap-3">
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600 text-white text-sm font-bold flex-shrink-0">
        {number}
      </div>
      <Icon size={18} className="text-indigo-500 flex-shrink-0" weight="duotone" />
      <h4 className="font-semibold text-gray-900 dark:text-white text-base">
        {title}
      </h4>
    </div>
    <div className="pl-11">{children}</div>
  </div>
);

const Callout = ({
  type,
  title,
  children,
}: {
  type: "warning" | "info" | "success" | "tip";
  title?: string;
  children: React.ReactNode;
}) => {
  const styles = {
    warning: {
      wrapper: "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800",
      icon: <Warning size={16} weight="fill" className="text-amber-500 flex-shrink-0 mt-0.5" />,
      title: "text-amber-800 dark:text-amber-300",
      body: "text-amber-700 dark:text-amber-400",
    },
    info: {
      wrapper: "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800",
      icon: <Info size={16} weight="fill" className="text-blue-500 flex-shrink-0 mt-0.5" />,
      title: "text-blue-800 dark:text-blue-300",
      body: "text-blue-700 dark:text-blue-400",
    },
    success: {
      wrapper: "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800",
      icon: <CheckCircle size={16} weight="fill" className="text-green-500 flex-shrink-0 mt-0.5" />,
      title: "text-green-800 dark:text-green-300",
      body: "text-green-700 dark:text-green-400",
    },
    tip: {
      wrapper: "bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800",
      icon: <Info size={16} weight="fill" className="text-violet-500 flex-shrink-0 mt-0.5" />,
      title: "text-violet-800 dark:text-violet-300",
      body: "text-violet-700 dark:text-violet-400",
    },
  };
  const s = styles[type];
  return (
    <div className={`rounded-lg border p-3 flex gap-2.5 ${s.wrapper}`}>
      {s.icon}
      <div>
        {title && <p className={`text-sm font-semibold mb-0.5 ${s.title}`}>{title}</p>}
        <div className={`text-sm leading-relaxed ${s.body}`}>{children}</div>
      </div>
    </div>
  );
};

const AppleSearchAdsGuide: React.FC = () => {
  const [fancyboxRef] = useFancybox({});

  return (
    <section
      id="apple-search-ads"
      className="scroll-mt-24 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-5 shadow-sm space-y-4"
      ref={fancyboxRef}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <div className="size-10 flex items-center justify-center bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-1.5">
          <img
            src={APP_PLATFORM_LOGO_MAP.app_store}
            alt="Apple Search Ads"
            className="w-full h-auto object-contain"
          />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
            Apple Search Ads Integration
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Connect your Apple Search Ads campaigns to AppRevBooster
          </p>
        </div>
      </div>

      {/* Intro */}
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
        This guide walks you through linking your{" "}
        <span className="font-semibold">Apple Search Ads</span> account to
        AppRevBooster. The integration uses OAuth 2.0 via a public/private key
        pair — AppRevBooster manages the private key on your behalf, so you only
        need to upload <span className="font-semibold">our public key</span> to
        Apple to generate three credential IDs.
      </p>

      <Callout type="warning" title="Important: Use an API Account Manager account">
        The <strong>API credentials screen</strong> in Apple Search Ads is{" "}
        <strong>only visible when signed in with an API Account Manager role</strong>.
        If you sign in with your main admin account, the API section may not appear at all.
        Follow Step 2 to create and use the correct account type.
      </Callout>

      {/* Prerequisites */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <CheckCircle size={18} weight="duotone" className="text-indigo-500" />
          Before You Begin (Prerequisites)
        </h3>
        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <li className="flex items-start gap-2">
            <span className="mt-1 w-4 h-4 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-[10px] font-bold flex-shrink-0">1</span>
            You must have <span className="font-semibold ml-1">Admin</span>-level access to your Apple Search Ads account (needed to invite the API user in Step 2).
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1 w-4 h-4 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-[10px] font-bold flex-shrink-0">2</span>
            The <span className="font-semibold ml-1">AppRevBooster integration modal</span> must be open so you can download the public key in Step 1.
          </li>
        </ul>
      </div>

      {/* Steps */}
      <div className="space-y-3">

        {/* Step 1 */}
        <StepCard number={1} icon={DownloadSimple} title="Download the Public Key from AppRevBooster">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            AppRevBooster provides a public key that you will upload to Apple Search Ads.
            You do <strong>not</strong> need to generate your own key pair.
          </p>
          <ol className="list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-1.5">
            <li>
              In AppRevBooster, navigate to{" "}
              <RedirectLink href="/integrations" text="Integrations → Revenue Sources → Add Account → Apple Search Ads" />.
            </li>
            <li>
              In <strong>Step 1</strong> of the modal, click{" "}
              <span className="font-semibold">Download Public Key (.pem)</span>.
            </li>
            <li>Save the <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">public-key.pem</code> file — you will upload it to Apple in Step 4.</li>
          </ol>
          <StepImage step="1" />
          <Callout type="info" title="Keep this modal open">
            After downloading the key, click <strong>"I've Generated the IDs"</strong> to proceed to Step 2 of the modal — but wait until you have completed this guide and have the three IDs ready.
          </Callout>
        </StepCard>

        {/* Step 2 */}
        <StepCard number={2} icon={UserPlus} title="Create an API Account Manager User (one-time setup)">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            This step requires admin rights. If you already have a dedicated{" "}
            <strong>API Account Manager</strong> user, skip to Step 3.
          </p>
          <ol className="list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-1.5">
            <li>
              Log in to{" "}
              <RedirectLink href="https://app.searchads.apple.com/" text="Apple Search Ads" />{" "}
              with your <strong>admin</strong> credentials.
            </li>
            <li>
              Navigate to:{" "}
              <RedirectLink
                href="https://app.searchads.apple.com/cm/app/settings/usermanagement"
                text="Account Settings → User Management → Users"
              />
              .
            </li>
            <li>Click <strong>Invite Users</strong>.</li>
            <li>Enter the email address of the user you want to grant API access to.</li>
            <li>
              Set the role to{" "}
              <span className="font-semibold text-indigo-700 dark:text-indigo-300">API Account Manager</span>{" "}
              — <em>not</em> Admin and <em>not</em> Read Only.
            </li>
            <li>Send the invite and accept it from the invitation email.</li>
          </ol>
          <Callout type="warning" title="Why not the admin account?">
            Apple Search Ads only shows the <strong>API credentials screen</strong> to users with the{" "}
            <strong>API Account Manager</strong> role. Logging in with your admin account may result in the API section not appearing at all.
          </Callout>
        </StepCard>

        {/* Step 3 */}
        <StepCard number={3} icon={SignIn} title="Sign In as the API Account Manager User">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            Sign out of your admin account and sign in with the API Account Manager credentials.
          </p>
          <ol className="list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-1.5">
            <li>Sign out of your current Apple Search Ads session.</li>
            <li>
              Sign in to{" "}
              <RedirectLink href="https://app.searchads.apple.com/" text="Apple Search Ads" />{" "}
              using the <strong>API Account Manager</strong> user's credentials.
            </li>
            <li>
              Navigate to:{" "}
              <RedirectLink
                href="https://app.searchads.apple.com/cm/app/settings/apicertificates"
                text="Account Settings → API → Client Credentials"
              />
              .
            </li>
          </ol>
        </StepCard>

        {/* Step 4 */}
        <StepCard number={4} icon={UploadSimple} title="Upload the Public Key & Generate Credentials">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            On the Client Credentials page, upload the public key file you downloaded from AppRevBooster.
          </p>
          <ol className="list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-1.5">
            <li>
              Click{" "}
              <strong>Create Client Credentials</strong>{" "}
              (or{" "}
              <RedirectLink
                href="https://app.searchads.apple.com/cm/app/settings/apicertificates"
                text="open the page directly"
              />
              ).
            </li>
            <li>
              Upload the{" "}
              <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">public-key.pem</code>{" "}
              file you downloaded from AppRevBooster in Step 1. Paste the full contents including the{" "}
              <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">-----BEGIN PUBLIC KEY-----</code> and{" "}
              <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">-----END PUBLIC KEY-----</code> headers.
            </li>
            <li>Click <strong>Create</strong>.</li>
            <li>
              Apple will generate and display three values — copy and save all three:
              <ul className="mt-2 space-y-1.5 pl-4 list-none">
                <li className="flex items-start gap-2">
                  <Key size={14} className="text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Client ID</strong>{" "}
                    <span className="text-gray-500 dark:text-gray-400 text-xs">
                      (e.g. <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">SEARCHADS.ad81b2c4-e1f9-...</code>)
                    </span>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Key size={14} className="text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Team ID</strong>{" "}
                    <span className="text-gray-500 dark:text-gray-400 text-xs">
                      (e.g. <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">SEARCHADS.9f2e4a6d-bc81-...</code>)
                    </span>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Key size={14} className="text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Key ID</strong>{" "}
                    <span className="text-gray-500 dark:text-gray-400 text-xs">
                      (e.g. <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">8a2d1e9c-4f7b-4d6e-...</code>)
                    </span>
                  </span>
                </li>
              </ul>
            </li>
          </ol>
          <StepImage step="2" />
          <Callout type="success" title="No private key needed">
            You do <strong>not</strong> need to create or download a private key.
            AppRevBooster manages the private key on your behalf — you only need to provide the three IDs above.
          </Callout>
        </StepCard>

        {/* Step 5 */}
        <StepCard number={5} icon={Copy} title="Enter the IDs in AppRevBooster">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            You now have everything needed to complete the integration in AppRevBooster.
          </p>
          <ol className="list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-1.5">
            <li>Return to the Apple Search Ads integration modal in AppRevBooster.</li>
            <li>
              Click <strong>"I've Generated the IDs"</strong> to advance to{" "}
              <strong>Step 2</strong> of the modal.
            </li>
            <li>
              Paste the three values you copied from Apple:
              <ul className="mt-1.5 list-disc pl-5 space-y-0.5 text-sm">
                <li><strong>Client ID</strong></li>
                <li><strong>Team ID</strong></li>
                <li><strong>Key ID</strong></li>
              </ul>
            </li>
            <li>Click <strong>Connect</strong> to save and finalize the integration.</li>
          </ol>
          <StepImage step="3" className="!w-auto h-[500px]" />
        </StepCard>
      </div>

      {/* Completion banner */}
      <Callout type="success" title="Integration Complete!">
        Your Apple Search Ads account is now connected to AppRevBooster. Campaign spend and
        attribution data will begin syncing automatically to your dashboard.
      </Callout>
    </section>
  );
};

export default AppleSearchAdsGuide;
